import { useState, useCallback } from 'react';

export const usePagination = (initialState = {}) => {
    const [pagination, setPagination] = useState({
        page: initialState.page || 1,
        limit: initialState.limit || 10,
        sortBy: initialState.sortBy || 'created_at:DESC',
        search: initialState.search || '',
        total: initialState.total || 0,
        totalPages: initialState.totalPages || 0,
        ...initialState
    });

    const [data, setData] = useState([]);
    const [meta, setMeta] = useState({});
    const [links, setLinks] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Update pagination berdasarkan response meta
    const updateFromResponse = useCallback((response) => {
        if (response.meta) {
            setPagination(prev => ({
                ...prev,
                page: response.meta.currentPage || 1,
                limit: response.meta.itemsPerPage || 10,
                total: response.meta.totalItems || 0,
                totalPages: response.meta.totalPages || 0,
                sortBy: response.meta.sortBy ?
                    `${response.meta.sortBy[0][0]}:${response.meta.sortBy[0][1]}` :
                    'created_at:DESC',
                search: response.meta.search || '',
            }));
            setMeta(response.meta);
        }

        if (response.links) {
            setLinks(response.links);
        }

        if (response.data) {
            setData(response.data);
        }
    }, []);

    // Update pagination manually
    const updatePagination = useCallback((updates) => {
        setPagination(prev => ({
            ...prev,
            ...updates
        }));
    }, []);

    // Go to specific page
    const goToPage = useCallback((page) => {
        setPagination(prev => ({
            ...prev,
            page
        }));
    }, []);

    // Change limit
    const changeLimit = useCallback((limit) => {
        setPagination(prev => ({
            ...prev,
            limit,
            page: 1
        }));
    }, []);

    // Change sort
    const changeSort = useCallback((field, order = 'DESC') => {
        setPagination(prev => ({
            ...prev,
            sortBy: `${field}:${order}`,
            page: 1
        }));
    }, []);

    // Toggle sort order
    const toggleSortOrder = useCallback((field) => {
        const [currentField, currentOrder] = pagination.sortBy.split(':');
        const newOrder = currentField === field && currentOrder === 'ASC' ? 'DESC' : 'ASC';

        setPagination(prev => ({
            ...prev,
            sortBy: `${field}:${newOrder}`,
            page: 1
        }));
    }, [pagination.sortBy]);

    // Search
    const search = useCallback((searchTerm) => {
        setPagination(prev => ({
            ...prev,
            search: searchTerm,
            page: 1
        }));
    }, []);

    // Reset pagination
    const resetPagination = useCallback(() => {
        setPagination({
            page: 1,
            limit: 10,
            sortBy: 'created_at:DESC',
            search: '',
            total: 0,
            totalPages: 0
        });
        setData([]);
        setMeta({});
        setLinks({});
    }, []);

    // Build query params untuk API call
    const buildQueryParams = useCallback(() => {
        const params = new URLSearchParams();

        if (pagination.page) params.append('page', pagination.page);
        if (pagination.limit) params.append('limit', pagination.limit);
        if (pagination.search) params.append('search', pagination.search);
        if (pagination.sortBy) params.append('sortBy', pagination.sortBy);

        return params.toString();
    }, [pagination]);

    // Get query object untuk API calls
    const getQueryObject = useCallback(() => {
        return {
            page: pagination.page,
            limit: pagination.limit,
            ...(pagination.search && { search: pagination.search }),
            ...(pagination.sortBy && { sortBy: pagination.sortBy }),
        };
    }, [pagination]);

    // Extract sort field dan order dari sortBy string
    const getSortInfo = useCallback(() => {
        if (!pagination.sortBy) return { field: null, order: null };
        const [field, order] = pagination.sortBy.split(':');
        return { field, order };
    }, [pagination.sortBy]);

    return {
        // State
        pagination,
        data,
        meta,
        links,
        loading,
        error,

        // Setters
        setData,
        setLoading,
        setError,
        setMeta,
        setLinks,

        // Actions
        updateFromResponse,
        updatePagination,
        goToPage,
        changeLimit,
        changeSort,
        toggleSortOrder,
        search,
        resetPagination,

        // Utilities
        buildQueryParams,
        getQueryObject,
        getSortInfo,

        // Computed values
        hasNextPage: pagination.page < pagination.totalPages,
        hasPreviousPage: pagination.page > 1,
        isFirstPage: pagination.page === 1,
        isLastPage: pagination.page === pagination.totalPages,

        // Pagination controls
        nextPage: () => goToPage(Math.min(pagination.page + 1, pagination.totalPages)),
        previousPage: () => goToPage(Math.max(pagination.page - 1, 1)),
        firstPage: () => goToPage(1),
        lastPage: () => goToPage(pagination.totalPages),

        // Current range
        startIndex: (pagination.page - 1) * pagination.limit + 1,
        endIndex: Math.min(pagination.page * pagination.limit, pagination.total),

        // Sort info
        currentSortField: getSortInfo().field,
        currentSortOrder: getSortInfo().order,
    };
};